import { splitReferentialGrid } from './referentialGrid'
import {
  attachRowMetaColumn,
  detachRowMetaColumn,
  stripReservedColumnsFromGrid,
  type ReferentialRowUpdateMeta,
} from './referentialRowMetaColumn'

export type ParsedReferentialGrid = {
  gridData: string[][]
  rowUpdatedAt: Record<number, ReferentialRowUpdateMeta>
}

export type { ReferentialRowUpdateMeta }

// Une cellule vide vaut `null` dans une grille stockée, et manque (trou du tableau) dans une ligne
// rendue par le worker d'import, qui n'écrit que les cellules présentes.
type RawGridMatrix = (string | null | undefined)[][]

function isStringMatrix(value: unknown): value is RawGridMatrix {
  return (
    Array.isArray(value) &&
    value.every(
      (row) =>
        Array.isArray(row) && row.every((cell) => typeof cell === 'string' || cell == null)
    )
  )
}

// `Array.from` parcourt aussi les trous, que `map` saute.
function toStringMatrix(grid: RawGridMatrix): string[][] {
  return grid.map((row) => Array.from(row, (cell) => cell ?? ''))
}

function parseRawGridMatrix(payload: RawGridMatrix): ParsedReferentialGrid {
  return detachRowMetaColumn(toStringMatrix(payload))
}

export function parsePersistedGridPayload(payload: unknown): ParsedReferentialGrid {
  if (isStringMatrix(payload)) {
    return parseRawGridMatrix(payload)
  }

  if (
    payload &&
    typeof payload === 'object' &&
    'grid' in payload &&
    isStringMatrix((payload as { grid: unknown }).grid)
  ) {
    return parseRawGridMatrix((payload as { grid: RawGridMatrix }).grid)
  }

  return { gridData: [['']], rowUpdatedAt: {} }
}

export function sanitizeImportedGridData(gridData: string[][]): string[][] {
  return stripReservedColumnsFromGrid(toStringMatrix(gridData))
}

export function buildPersistedGridPayload(
  gridData: string[][],
  rowUpdatedAt: Record<number, ReferentialRowUpdateMeta>
): string[][] {
  return attachRowMetaColumn(gridData, rowUpdatedAt)
}

export function getPersistedBusinessGrid(gridData: string[][]): string[][] {
  return detachRowMetaColumn(gridData).gridData
}

export function getPersistedGridHeaders(gridData: string[][]): string[] {
  return splitReferentialGrid(getPersistedBusinessGrid(gridData)).headers
}
